import { useTranslation } from "react-i18next";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Info, Search, X, Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useAppearance } from "../contexts/AppearanceContext";

const maskCnpj = (v) => {
  v = v.replace(/\D/g, "").slice(0, 14);
  if (v.length > 12) return `${v.slice(0,2)}.${v.slice(2,5)}.${v.slice(5,8)}/${v.slice(8,12)}-${v.slice(12)}`;
  if (v.length > 8) return `${v.slice(0,2)}.${v.slice(2,5)}.${v.slice(5,8)}/${v.slice(8)}`;
  if (v.length > 5) return `${v.slice(0,2)}.${v.slice(2,5)}.${v.slice(5)}`;
  if (v.length > 2) return `${v.slice(0,2)}.${v.slice(2)}`;
  return v;
};

const maskCpf = (v) => {
  v = v.replace(/\D/g, "").slice(0, 11);
  if (v.length > 9) return `${v.slice(0,3)}.${v.slice(3,6)}.${v.slice(6,9)}-${v.slice(9)}`;
  if (v.length > 6) return `${v.slice(0,3)}.${v.slice(3,6)}.${v.slice(6)}`;
  if (v.length > 3) return `${v.slice(0,3)}.${v.slice(3)}`;
  return v;
};

const maskPhone = (v) => {
  v = v.replace(/\D/g, "").slice(0, 11);
  if (v.length > 6) return v.length === 11 ? `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}` : v.length === 10 ? `(${v.slice(0,2)}) ${v.slice(2,6)}-${v.slice(6)}` : `(${v.slice(0,2)}) ${v.slice(2)}`;
  if (v.length > 2) return `(${v.slice(0,2)}) ${v.slice(2)}`;
  if (v.length > 0) return `(${v}`;
  return v;
};

function OptionSearchSelect({ options, value, multiple, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50  = appearance.colors?.["50"]  || "#EFF6FF";

  const selected = multiple ? (Array.isArray(value) ? value : value ? [value] : []) : [];
  const singleVal = multiple ? "" : (value || "");
  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

  const toggleOption = (opt) => {
    if (multiple) {
      const arr = [...selected];
      const idx = arr.indexOf(opt);
      if (idx >= 0) arr.splice(idx, 1); else arr.push(opt);
      onChange(arr);
    } else {
      onChange(opt);
      setOpen(false);
      setSearch("");
    }
  };

  const removeChip = (e, opt) => { e.stopPropagation(); onChange(selected.filter(s => s !== opt)); };

  // ── Single select — uses Radix Popover (works inside Dialogs natively) ──
  if (!multiple) {
    return (
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
        <PopoverTrigger asChild>
          <div className="flex items-center gap-2 w-full min-h-9 px-3 py-1 rounded-md border border-slate-300 bg-white cursor-pointer hover:border-slate-400 transition-colors overflow-hidden">
            {singleVal ? (
              <>
                <span className="text-sm font-medium text-slate-800 flex-1 truncate">{singleVal}</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onChange(""); }} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
                  <X className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <>
                <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-400 flex-1 truncate">{t("common.select") || "Selecionar..."}</span>
              </>
            )}
            <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width]" onOpenAutoFocus={(e) => e.preventDefault()}>
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md outline-none focus:border-slate-400 bg-white"
              />
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-400 text-center">{t("common.noOption")}</div>
            ) : filtered.map(opt => {
              const isSelected = singleVal === opt;
              return (
                <button key={opt} type="button" onClick={() => toggleOption(opt)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-slate-50 border-b border-slate-50 last:border-0"
                  style={isSelected ? { backgroundColor: tc50 } : undefined}>
                  <span className={`flex-1 truncate ${isSelected ? "font-medium text-slate-900" : "text-slate-700"}`}>
                    {opt}
                  </span>
                  {isSelected && <Check className="w-4 h-4 flex-shrink-0" style={{ color: tc500 }} />}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // ── Multiple select (checkbox style) ──

  // Multi selected - chips inside field
  if (selected.length > 0 && !open) {
    return (
      <div className="flex flex-wrap items-center gap-1 w-full min-h-[36px] px-2 py-1 rounded-md border border-slate-300 bg-white cursor-pointer" onClick={() => setOpen(true)}>
        {selected.map(s => (
          <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs" style={{ backgroundColor: `${tc500}15`, color: tc500 }}>
            {s}<button type="button" onClick={(e) => removeChip(e, s)}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }} onFocus={() => setOpen(true)} placeholder="Buscar..." className="pl-9" />
      </div>
      {open && (
        <div className="absolute z-50 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto top-full mt-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-400 text-center">{t("common.noOption")}</div>
          ) : filtered.map(opt => {
            const isSelected = selected.includes(opt);
            return (
              <button key={opt} type="button" onClick={() => toggleOption(opt)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50">
                <div className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0" style={isSelected ? { borderColor: tc500, backgroundColor: tc500 } : { borderColor: "#cbd5e1" }}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className={isSelected ? "font-medium text-slate-900" : "text-slate-700"}>{opt}</span>
              </button>
            );
          })}
        </div>
      )}
      {open && <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearch(""); }} />}
    </div>
  );
}

export default function CustomFieldsForm({fields, values, onChange, layout }) {
  if (!fields || fields.length === 0) return null;

  const handleChange = (fieldName, value) => {
    onChange({ ...values, [fieldName]: value });
  };

  // Sort fields by layout order if available
  let sortedFields = [...fields];
  if (layout && layout.length > 0) {
    const orderMap = {};
    const widthMap = {};
    layout.forEach((l, i) => { orderMap[l.field_name] = l.order ?? i; widthMap[l.field_name] = l.field_width || "100"; });
    sortedFields.sort((a, b) => (orderMap[a.field_name] ?? 999) - (orderMap[b.field_name] ?? 999));
    sortedFields = sortedFields.map(f => ({ ...f, _width: widthMap[f.field_name] || "100" }));
  }

  // Group fields into rows (pair 50% fields together)
  const rows = [];
  let i = 0;
  while (i < sortedFields.length) {
    const f = sortedFields[i];
    if (f._width === "50" && i + 1 < sortedFields.length && sortedFields[i + 1]._width === "50") {
      rows.push([f, sortedFields[i + 1]]);
      i += 2;
    } else {
      rows.push([f]);
      i += 1;
    }
  }

  const renderField = (field) => {
    const val = values?.[field.field_name] ?? "";
    return (
      <div key={field.id}>
        {field.field_type !== "boolean" && (
          <Label className="text-slate-700 text-sm flex items-center gap-1.5">
            {field.field_label}
            {field.required && <span className="text-rose-500">*</span>}
            {field.help_text && (
              <Tooltip><TooltipTrigger asChild><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent className="max-w-xs">{field.help_text}</TooltipContent></Tooltip>
            )}
          </Label>
        )}
            {field.field_type === "text" && (
              <Input data-testid={`cf-${field.field_name}`} value={val} onChange={(e) => handleChange(field.field_name, e.target.value)} required={field.required} />
            )}
            {field.field_type === "textarea" && (
              <Textarea data-testid={`cf-${field.field_name}`} value={val} onChange={(e) => handleChange(field.field_name, e.target.value)} required={field.required} rows={3} />
            )}
            {field.field_type === "number" && (
              <Input data-testid={`cf-${field.field_name}`} type="number" step="any" value={val} onChange={(e) => handleChange(field.field_name, e.target.value)} required={field.required} />
            )}
            {field.field_type === "currency" && (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{field.currency_symbol || "R$"}</span>
                <Input data-testid={`cf-${field.field_name}`} className="pl-10" value={val} onChange={(e) => {
                  const fmt = field.currency_format || "1.000,00";
                  let v = e.target.value.replace(/[^\d]/g, "");
                  if (!v) { handleChange(field.field_name, ""); return; }
                  let num = parseInt(v, 10) / 100;
                  let formatted;
                  if (fmt === "1,000.00") {
                    formatted = num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  } else {
                    formatted = num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  }
                  handleChange(field.field_name, formatted);
                }} required={field.required} placeholder={field.currency_format === "1,000.00" ? "0.00" : "0,00"} />
              </div>
            )}
            {field.field_type === "date" && (
              <Input data-testid={`cf-${field.field_name}`} type="date" value={val} onChange={(e) => handleChange(field.field_name, e.target.value)} required={field.required} />
            )}
            {field.field_type === "datetime" && (
              <Input data-testid={`cf-${field.field_name}`} type="datetime-local" value={val} onChange={(e) => handleChange(field.field_name, e.target.value)} required={field.required} />
            )}
            {field.field_type === "email" && (
              <Input data-testid={`cf-${field.field_name}`} type="email" value={val} onChange={(e) => handleChange(field.field_name, e.target.value)} required={field.required} placeholder="email@exemplo.com" />
            )}
            {field.field_type === "cnpj" && (
              <Input data-testid={`cf-${field.field_name}`} value={val} onChange={(e) => handleChange(field.field_name, maskCnpj(e.target.value))} required={field.required} placeholder="00.000.000/0000-00" maxLength={18} />
            )}
            {field.field_type === "cpf" && (
              <Input data-testid={`cf-${field.field_name}`} value={val} onChange={(e) => handleChange(field.field_name, maskCpf(e.target.value))} required={field.required} placeholder="000.000.000-00" maxLength={14} />
            )}
            {field.field_type === "select" && (
              <OptionSearchSelect
                options={field.options || []}
                value={val}
                multiple={field.select_multiple}
                onChange={(v) => handleChange(field.field_name, v)}
              />
            )}
            {field.field_type === "boolean" && (
              <div className="flex flex-col gap-2">
                <Label className="text-slate-700 text-sm flex items-center gap-1.5">
                  {field.field_label}
                  {field.required && <span className="text-rose-500">*</span>}
                  {field.help_text && (
                    <Tooltip><TooltipTrigger asChild><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent className="max-w-xs">{field.help_text}</TooltipContent></Tooltip>
                  )}
                </Label>
                <Switch data-testid={`cf-${field.field_name}`} checked={val === true || val === "true"} onCheckedChange={(checked) => handleChange(field.field_name, checked)} />
              </div>
            )}
          </div>
        );
  };

  return (
    <div className="space-y-3">
      {rows.map((row, ri) => (
        row.length === 2 ? (
          <div key={ri} className="grid grid-cols-2 gap-3">
            {row.map(f => renderField(f))}
          </div>
        ) : renderField(row[0])
      ))}
    </div>
  );
}
